// eslint-disable-next-line jira/restricted/react
import React, { type ComponentType, PureComponent, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import PreferencesIcon from '@atlaskit/icon/glyph/preferences';
import InlineDialog from '@atlaskit/inline-dialog';
import AnalyticsData from '@atlassian/jira-analytics-web-react/src/components/analytics-data.tsx';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { INLINE_DIALOG, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import commonMessages from '@atlassian/jira-servicedesk-common/src/i18n';
import {
	SUBMIT_COMPLETED_STATUS,
	type SubmitStatus,
} from '@atlassian/jira-servicedesk-common/src/ui/components/submit-button/model';
import SubmitButton from '@atlassian/jira-servicedesk-common/src/ui/components/submit-button/view.tsx';
import { ANALYTIC_CONTEXTS } from '@atlassian/jira-servicedesk-common/src/utils/analytics';
import { Footer, TitleComponent, Wrapper } from './styled';

export type Props = {
	isOpen: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ContentComponent: ComponentType<any>;
	storedRunAsUserAccountId: string;
	submitStatus: SubmitStatus;
	onSave: (arg1: string, arg2: UIAnalyticsEvent | undefined) => void;
	onClose: () => void;
	onToggle: (arg1: boolean) => void;
	intl: IntlShape;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Header = (props: any) => (
	<Heading size="small" as="h2">
		<TitleComponent {...props} />
	</Heading>
);

type State = {
	selectedUserAccountId: string;
};

// eslint-disable-next-line jira/react/no-class-components
class SettingsDialog extends PureComponent<Props, State> {
	state = {
		selectedUserAccountId: this.props.storedRunAsUserAccountId,
	};

	onToggle = (_: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'configurationButton');
		this.props.onToggle(this.props.isOpen);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onUserSelected = (selectedUser: any) => {
		this.setState({
			selectedUserAccountId: selectedUser.accountId,
		});
	};

	onSave = (_: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		this.props.onSave(this.state.selectedUserAccountId, analyticsEvent);
	};

	renderDialogBody() {
		const {
			intl: { formatMessage },
			ContentComponent,
			onClose,
			storedRunAsUserAccountId,
			submitStatus,
		} = this.props;
		const { selectedUserAccountId } = this.state;
		const isDirty = selectedUserAccountId !== storedRunAsUserAccountId;

		return (
			<Wrapper data-test-id="servicedesk-automation-summary.layout.header.settings-dialog.content">
				<ViewTracker />
				<Header>{formatMessage(commonMessages.configuration)}</Header>
				<ContentComponent onSelect={this.onUserSelected} />
				<Footer>
					<SubmitButton
						onSubmit={this.onSave}
						// Re-enable the button for the brief moment where the submit has completed and the state is no longer dirty
						isDisabled={!isDirty && submitStatus !== SUBMIT_COMPLETED_STATUS}
						submitStatus={submitStatus}
					/>
					<Button appearance="link" onClick={onClose} isDisabled={submitStatus !== null}>
						{formatMessage(commonMessages.cancel)}
					</Button>
				</Footer>
			</Wrapper>
		);
	}

	render() {
		const {
			intl: { formatMessage },
			isOpen,
		} = this.props;
		return (
			<InlineDialog isOpen={isOpen} placement="bottom-end" content={this.renderDialogBody()}>
				<Button
					onClick={this.onToggle}
					iconAfter={<PreferencesIcon label={formatMessage(commonMessages.configuration)} />}
				/>
			</InlineDialog>
		);
	}
}

const SettingsDialogWithAnalytics: ComponentType<Props> = AnalyticsSource(
	'editDefaultUser',
	INLINE_DIALOG,
)(
	AnalyticsData({
		attributes: {
			context: ANALYTIC_CONTEXTS.AUTOMATION_SETTINGS,
		},
	})(injectIntl(SettingsDialog)),
);

export default SettingsDialogWithAnalytics;
